import { useEffect, useState } from 'react';
import { api, type FiledContract } from '../lib/api';
import { prettyKind } from './documents/libraryKinds';
import DocumentChangesDialog, { ChangesButton, useChangeSummaries } from './documents/DocumentChanges';

/**
 * FILED-LABEL (the 119th): what a contract is, by its stored category - a generated AIR contract or a PSA.
 * FILED-LEASE (the 120th, his YES): and a signed lease (`lease`, the upload's "Lease Agreement") - never a
 * `lease_proposal`, which is an offer to lease, not a contract. And a signed listing agreement (LD1, the 120th).
 */
const CONTRACT_KINDS = new Set(['contract', 'psa', 'lease', 'listing_agreement']);
const isContract = (c: FiledContract) => c.category != null && CONTRACT_KINDS.has(c.category);

/**
 * Every AIR contract already filed against a deal.
 *
 * 🚨 WHY THIS EXISTS. `generateContract` files against `target_type: 'deal'`, and until
 * `GET /api/contracts/deal/:dealId` was added nothing read that target, so a generated contract
 * was write-only the moment the operator left the success banner that made it. (Since B48 the
 * Deal Room tab reads the same `deal` target, so contracts appear there too; this stays the
 * contract-specific view with the ready-to-click signed URLs.)
 *
 * Shared by the deal page and the contract builder deliberately: two hand-kept copies of "what
 * has been filed against this deal" is the drift this repo keeps paying for.
 */
export default function DealContracts({
  dealId,
  refreshKey,
  only,
  onOpenChanges,
}: {
  dealId: string;
  /** Change to force a re-read - the builder passes the just-filed contract so it joins the list. */
  refreshKey?: string;
  /**
   * ⛔ FILED-LABEL (the 119th): this list was headed "Filed contracts" while showing EVERY file on the deal (LOIs, an
   *    investment analysis, and since AIW2 the OMs). A control names its contents: by default it is "Filed on this
   *    deal", each file named by its kind, contracts first, nothing hidden. The contract builder asks "what contracts
   *    have I sent", so it passes `only="contracts"`.
   */
  only?: 'contracts';
  /**
   * DOCTRACK part 4: what a row's "N changes" opens. The deal page passes its `?doc=` opener so the comparison has a
   * link; anywhere else this list opens the comparison itself.
   */
  onOpenChanges?: (filingId: string) => void;
}) {
  /**
   * Three states, not two. `null` is "we have not found out yet or could not", and it must never
   * render as "nothing has been filed" - one of those means the operator has sent no contract,
   * the other means we do not know what he has sent, and on this record they are not the same
   * sentence.
   */
  const [contracts, setContracts] = useState<FiledContract[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [changesOf, setChangesOf] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    (async () => {
      try {
        const { data } = await api.listDealContracts(dealId);
        if (!cancelled) setContracts(data);
      } catch {
        if (!cancelled) { setContracts(null); setFailed(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, refreshKey]);

  const contractsOnly = only === 'contracts';
  const shown = contracts === null ? null
    : contractsOnly ? contracts.filter(isContract)
    : [...contracts.filter(isContract), ...contracts.filter((c) => !isContract(c))];
  const changes = useChangeSummaries(shown ? shown.map((c) => c.linkId) : []);
  const openChanges = onOpenChanges ?? setChangesOf;

  return (
    <section data-testid="filed-contracts">
      <h2 className="text-sm font-semibold text-gray-900 mb-2">{contractsOnly ? 'Filed contracts' : 'Filed on this deal'}</h2>
      {failed ? (
        <p className="text-sm text-gray-600">{contractsOnly ? 'Could not load the filed contracts.' : 'Could not load what is filed on this deal.'}</p>
      ) : shown === null ? (
        <p className="text-sm text-gray-500">Checking what has already been filed…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-600">{contractsOnly ? 'No contract has been filed against this deal yet.' : 'Nothing has been filed on this deal yet.'}</p>
      ) : (
        <ul className="space-y-1">
          {shown.map((c) => (
            <li key={c.linkId} className="text-sm">
              {!contractsOnly && <span className="text-gray-500">{prettyKind(c.category ?? 'other')}: </span>}
              <a href={c.url} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">
                {c.title}
              </a>
              {c.filedAt && (
                <span className="text-gray-500"> — filed {new Date(c.filedAt).toLocaleString()}</span>
              )}
              {changes.byLink[c.linkId] && (
                <>
                  <span className="text-gray-500"> · </span>
                  <ChangesButton summary={changes.byLink[c.linkId]} onOpen={openChanges} />
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {changes.failed && <p className="mt-1 text-xs text-gray-500">Could not check these documents for changes.</p>}
      {changesOf && <DocumentChangesDialog filingId={changesOf} onClose={() => setChangesOf(null)} />}
    </section>
  );
}
